import React, { useEffect, useState } from 'react';
import { getAllAppointments, cancelAppointment, rescheduleAppointment, getAvailableSlots, completeAppointment, noShowAppointment } from '../api';
import { formatDate, formatTimeRange } from '../utils/format';
import styles from './Page.module.css';
import { useAuth } from '../AuthContext';

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const [rescheduleAppt, setRescheduleAppt] = useState(null);
  const [newDate, setNewDate] = useState('');
  const [newSlots, setNewSlots] = useState([]);
  const [selectedNewSlot, setSelectedNewSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleMsg, setRescheduleMsg] = useState('');
  const [noSlots, setNoSlots] = useState(false);

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 60);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  useEffect(() => { fetchAppointments(); }, []);

  const fetchAppointments = async () => {
    setLoading(true);
    const res = await getAllAppointments();
    let data = [...res.data].sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));
    if (user?.role === 'DOCTOR' && user?.doctorId) {
      data = data.filter(a => a.doctor?.id === user.doctorId);
    }
    setAppointments(data);
    setLoading(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Cancel this appointment?')) return;
    await cancelAppointment(id);
    fetchAppointments();
  };

  const handleMarkComplete = async (id) => {
    if (!window.confirm('Mark this appointment as completed?')) return;
    try {
      await completeAppointment(id);
      fetchAppointments();
    } catch { alert('Failed to mark as completed.'); }
  };

  const handleNoShow = async (id) => {
    if (!window.confirm('Mark patient as No Show?')) return;
    try {
      await noShowAppointment(id);
      fetchAppointments();
    } catch { alert('Failed to mark as No Show.'); }
  };

  const openReschedule = (appt) => {
    setRescheduleAppt(appt);
    setNewDate('');
    setNewSlots([]);
    setSelectedNewSlot(null);
    setRescheduleMsg('');
    setNoSlots(false);
  };

  const closeReschedule = () => {
    setRescheduleAppt(null);
    setNewDate('');
    setNewSlots([]);
    setSelectedNewSlot(null);
    setRescheduleMsg('');
  };

  const handleFetchNewSlots = async () => {
    if (!newDate || !rescheduleAppt) return;
    setSlotsLoading(true);
    setNewSlots([]);
    setSelectedNewSlot(null);
    setNoSlots(false);
    try {
      const res = await getAvailableSlots(rescheduleAppt.doctor.id, newDate);
      if (res.data.length === 0) setNoSlots(true);
      else setNewSlots(res.data);
    } catch { setNoSlots(true); }
    setSlotsLoading(false);
  };

  const handleReschedule = async () => {
    if (!selectedNewSlot || !newDate) return;
    setRescheduleLoading(true);
    setRescheduleMsg('');
    try {
      await rescheduleAppointment(rescheduleAppt.id, {
        appointmentDate: newDate,
        slot: { id: selectedNewSlot.id },
      });
      setRescheduleMsg('Appointment rescheduled successfully!');
      fetchAppointments();
      setTimeout(() => closeReschedule(), 1500);
    } catch {
      setRescheduleMsg('Failed to reschedule. Please try again.');
    }
    setRescheduleLoading(false);
  };

  const filtered = appointments.filter(a => {
    const matchStatus = filter === 'ALL' || a.status === filter;
    const matchSearch = search === '' ||
      a.patient?.name?.toLowerCase().includes(search.toLowerCase()) ||
      a.patient?.phone?.includes(search) ||
      a.doctor?.name?.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const statusColor = (s) => {
    switch (s) {
      case 'BOOKED':    return '#2e7d32';
      case 'COMPLETED': return '#1565c0';
      case 'CANCELLED': return '#c0272d';
      case 'NO_SHOW':   return '#e65100';
      default:          return '#f57f17';
    }
  };

  const renderActions = (a) => {
    const isPast  = a.appointmentDate < today;
    const isToday = a.appointmentDate === today;

    if (a.status === 'BOOKED') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          {/* Today or past — show Complete */}
          {(isToday || isPast) && (
            <button
              onClick={() => handleMarkComplete(a.id)}
              style={{
                fontSize: 12, padding: '4px 10px',
                background: '#e8f5e9', color: '#2e7d32',
                border: '1.5px solid #2e7d32',
                borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              }}>
              ✓ Complete
            </button>
          )}
          {/* Only past — show No Show */}
          {isPast && (
            <button
              onClick={() => handleNoShow(a.id)}
              style={{
                fontSize: 12, padding: '4px 10px',
                background: '#fff3e0', color: '#e65100',
                border: '1.5px solid #e65100',
                borderRadius: 6, cursor: 'pointer', fontWeight: 600,
              }}>
              ✗ No Show
            </button>
          )}
          {/* Future — show Reschedule + Cancel */}
          {!isPast && !isToday && (
            <>
              <button className={styles.editBtn} onClick={() => openReschedule(a)}>
                Reschedule
              </button>
              <button className={styles.deleteBtn} onClick={() => handleCancel(a.id)}>
                Cancel
              </button>
            </>
          )}
          {/* Today — also allow cancel */}
          {isToday && (
            <button className={styles.deleteBtn} onClick={() => handleCancel(a.id)}>
              Cancel
            </button>
          )}
        </div>
      );
    }

    if (a.status === 'COMPLETED')
      return <span style={{ fontSize: 12, color: '#1565c0', fontWeight: 600 }}>✓ Completed</span>;
    if (a.status === 'NO_SHOW')
      return <span style={{ fontSize: 12, color: '#e65100', fontWeight: 600 }}>✗ No Show</span>;
    if (a.status === 'CANCELLED')
      return <span style={{ fontSize: 12, color: '#c0272d', fontWeight: 600 }}>✗ Cancelled</span>;

    return <span style={{ fontSize: 12, color: '#aaa' }}>—</span>;
  };

  return (
    <div>
      <h2 className={styles.heading}>All Appointments</h2>

      {/* Stats */}
      <div className={styles.statsRow}>
        {['ALL', 'BOOKED', 'COMPLETED', 'NO_SHOW', 'CANCELLED'].map(s => (
          <div key={s}
            className={`${styles.statCard} ${filter === s ? styles.statActive : ''}`}
            onClick={() => setFilter(s)}
            style={{ cursor: 'pointer' }}>
            <strong style={{ color: s === 'ALL' ? '#1a3a6b' : statusColor(s) }}>
              {s === 'ALL' ? appointments.length
                : appointments.filter(a => a.status === s).length}
            </strong>
            <span>{s === 'NO_SHOW' ? 'NO SHOW' : s}</span>
          </div>
        ))}
      </div>

      {/* Search */}
      <div className={styles.searchBar}>
        <input
          placeholder="Search by patient name, phone or doctor name..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Table */}
      <div className={styles.card}>
        {loading ? (
          <p style={{ textAlign: 'center', padding: 20 }}>Loading...</p>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>#</th>
                <th>Patient</th>
                <th>Phone</th>
                <th>Doctor</th>
                <th>Specialization</th>
                <th>Date</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: 20, color: '#888' }}>
                    No appointments found
                  </td>
                </tr>
              ) : filtered.map((a, i) => (
                <tr key={a.id}>
                  <td>{i + 1}</td>
                  <td>
                    <strong>{a.patient?.name || '-'}</strong>
                    {a.patient?.gender && a.patient?.age && (
                      <><br /><small style={{ color: '#888' }}>{a.patient.gender}, {a.patient.age} yrs</small></>
                    )}
                  </td>
                  <td>
                    <span style={{ color: '#1a3a6b', fontWeight: 500 }}>
                      {a.patient?.phone || '-'}
                    </span>
                  </td>
                  <td>{a.doctor?.name || '-'}</td>
                  <td>{a.doctor?.specialization || '-'}</td>
                  <td>{formatDate(a.appointmentDate)}</td>
                  <td>{formatTimeRange(a.slot?.startTime, a.slot?.endTime)}</td>
                  <td>
                    <span className={styles.badge}
                      style={{
                        background: statusColor(a.status) + '20',
                        color: statusColor(a.status),
                      }}>
                      {a.status === 'NO_SHOW' ? 'NO SHOW' : a.status}
                    </span>
                  </td>
                  <td>{renderActions(a)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Reschedule Modal */}
      {rescheduleAppt && (
        <div style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center',
          justifyContent: 'center', zIndex: 1000,
        }}>
          <div style={{
            background: '#fff', borderRadius: 14,
            padding: 28, width: '100%', maxWidth: 480,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
          }}>
            <h3 style={{ color: '#1a3a6b', marginBottom: 16, fontSize: 17 }}>
              Reschedule Appointment
            </h3>

            <div style={{
              background: '#e8edf6', borderRadius: 8,
              padding: '10px 14px', marginBottom: 18, fontSize: 13,
            }}>
              <p style={{ margin: '0 0 4px' }}><strong>Patient:</strong> {rescheduleAppt.patient?.name}</p>
              <p style={{ margin: '0 0 4px' }}><strong>Doctor:</strong> {rescheduleAppt.doctor?.name}</p>
              <p style={{ margin: 0 }}>
                <strong>Current:</strong> {formatDate(rescheduleAppt.appointmentDate)} at {formatTimeRange(rescheduleAppt.slot?.startTime, rescheduleAppt.slot?.endTime)}
              </p>
            </div>

            <div style={{ marginBottom: 12 }}>
              <label style={{
                display: 'block', fontSize: 13,
                fontWeight: 600, color: '#1a3a6b', marginBottom: 5,
              }}>
                New Date *
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={newDate}
                  min={today} max={maxDateStr}
                  onChange={e => {
                    setNewDate(e.target.value);
                    setNewSlots([]);
                    setSelectedNewSlot(null);
                    setNoSlots(false);
                  }}
                  style={{
                    flex: 1, padding: '9px 12px',
                    border: '1.5px solid #e0e4ed',
                    borderRadius: 7, fontSize: 14, outline: 'none',
                  }} />
                <button onClick={handleFetchNewSlots}
                  disabled={!newDate || slotsLoading}
                  style={{
                    background: '#1a3a6b', color: '#fff',
                    border: 'none', borderRadius: 7,
                    padding: '9px 14px', fontSize: 13,
                    fontWeight: 600, cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}>
                  {slotsLoading ? 'Loading...' : 'Get Slots'}
                </button>
              </div>
            </div>

            {noSlots && (
              <p style={{ color: '#c0272d', fontSize: 13, marginBottom: 10 }}>
                No available slots for this date.
              </p>
            )}

            {newSlots.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <label style={{
                  display: 'block', fontSize: 13,
                  fontWeight: 600, color: '#1a3a6b', marginBottom: 6,
                }}>
                  Select New Time Slot *
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {newSlots.map(slot => (
                    <span key={slot.id}
                      onClick={() => setSelectedNewSlot(slot)}
                      style={{
                        padding: '5px 12px', borderRadius: 6,
                        fontSize: 13, fontWeight: 500, cursor: 'pointer',
                        background: selectedNewSlot?.id === slot.id ? '#c0272d' : '#e8edf6',
                        color: selectedNewSlot?.id === slot.id ? '#fff' : '#1a3a6b',
                        border: selectedNewSlot?.id === slot.id
                          ? '2px solid #c0272d' : '2px solid transparent',
                        transition: 'all 0.2s',
                      }}>
                      {formatTimeRange(slot.startTime, slot.endTime)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rescheduleMsg && (
              <p style={{
                fontSize: 13, fontWeight: 500, marginBottom: 10,
                color: rescheduleMsg.includes('success') ? '#2e7d32' : '#c0272d',
              }}>
                {rescheduleMsg}
              </p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button onClick={handleReschedule}
                disabled={!selectedNewSlot || rescheduleLoading}
                style={{
                  flex: 1, background: '#c0272d', color: '#fff',
                  border: 'none', borderRadius: 8, padding: '10px',
                  fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  opacity: !selectedNewSlot ? 0.6 : 1,
                }}>
                {rescheduleLoading ? 'Rescheduling...' : 'Confirm Reschedule'}
              </button>
              <button onClick={closeReschedule}
                style={{
                  flex: 1, background: 'transparent', color: '#1a3a6b',
                  border: '1.5px solid #1a3a6b', borderRadius: 8,
                  padding: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
